import { observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { TimeUnit } from 'Util/TimeUnit';

export interface IBackgroundJobConfigEntityAttributes extends IModelAttributes {
	sendReminderEmailOne: boolean;
	sendEmailReminderTwo: boolean;
	reminderEmailOneDaysBeforeDeparture: number;
	reminderEmailTwoDaysBeforeDeparture: number;
	sendTransactionReport: boolean;
	automaticallyCancelBookingsNotCheckedIn: boolean;
	cancelNotCheckedInBookingsAfterDepartureValue: number;
	cancelNotCheckedInBookingsAfterDepartureUnit: TimeUnit;
	sendThankYouEmail?: boolean;
	sendThankYouEmailNumberOfDaysAfterDeparture?: number;
}
@entity('BackgroundJobConfigEntity', 'Automatic Background Tasks')
export default class BackgroundJobConfigEntity extends Model implements IBackgroundJobConfigEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * Whether or not to send reminder email one to customers.
	 */
	@observable
	@attribute<BackgroundJobConfigEntity, boolean>()
	public sendReminderEmailOne: boolean;

	/**
	 * Whether or not to send reminder email two to customers.
	 */
	@observable
	@attribute<BackgroundJobConfigEntity, boolean>()
	public sendEmailReminderTwo: boolean;

	/**
	 * The number of days before departure that reminder email one will be triggered.
	 */
	@observable
	@attribute<BackgroundJobConfigEntity, number>()
	public reminderEmailOneDaysBeforeDeparture: number;

	/**
	 * The number of days before departure that reminder email two will be triggered.
	 */
	@observable
	@attribute<BackgroundJobConfigEntity, number>()
	public reminderEmailTwoDaysBeforeDeparture: number;

	/**
	 * The email address that will receive the transaction report daily.
	 */
	@observable
	@attribute<BackgroundJobConfigEntity, boolean>()
	public sendTransactionReport: boolean;

	/**
	 * Cancel bookings that have not been checked in within a certain time since departure.
	 */
	@observable
	@attribute<BackgroundJobConfigEntity, boolean>()
	public automaticallyCancelBookingsNotCheckedIn: boolean;

	/**
	 * How long after departure should the non-checked-in bookings be cancelled?
	 */
	@observable
	@attribute<BackgroundJobConfigEntity, number>()
	public cancelNotCheckedInBookingsAfterDepartureValue: number;

	@observable
	@attribute<BackgroundJobConfigEntity, TimeUnit>()
	public cancelNotCheckedInBookingsAfterDepartureUnit: TimeUnit = TimeUnit.HOURS;

	@observable
	@attribute<BackgroundJobConfigEntity, boolean>()
	public sendThankYouEmail?: boolean;

	@observable
	@attribute<BackgroundJobConfigEntity, number>()
	public sendThankYouEmailNumberOfDaysAfterDeparture?: number;

	constructor(attributes?: Partial<IBackgroundJobConfigEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IBackgroundJobConfigEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.sendReminderEmailOne !== undefined) {
				this.sendReminderEmailOne = attributes.sendReminderEmailOne;
			}
			if (attributes.sendEmailReminderTwo !== undefined) {
				this.sendEmailReminderTwo = attributes.sendEmailReminderTwo;
			}
			if (attributes.reminderEmailOneDaysBeforeDeparture !== undefined) {
				this.reminderEmailOneDaysBeforeDeparture = attributes.reminderEmailOneDaysBeforeDeparture;
			}
			if (attributes.reminderEmailTwoDaysBeforeDeparture !== undefined) {
				this.reminderEmailTwoDaysBeforeDeparture = attributes.reminderEmailTwoDaysBeforeDeparture;
			}
			if (attributes.sendTransactionReport !== undefined) {
				this.sendTransactionReport = attributes.sendTransactionReport;
			}
			if (attributes.automaticallyCancelBookingsNotCheckedIn !== undefined) {
				this.automaticallyCancelBookingsNotCheckedIn = attributes.automaticallyCancelBookingsNotCheckedIn;
			}
			if (attributes.cancelNotCheckedInBookingsAfterDepartureValue !== undefined) {
				this.cancelNotCheckedInBookingsAfterDepartureValue =
					attributes.cancelNotCheckedInBookingsAfterDepartureValue;
			}
			if (attributes.cancelNotCheckedInBookingsAfterDepartureUnit !== undefined) {
				this.cancelNotCheckedInBookingsAfterDepartureUnit =
					attributes.cancelNotCheckedInBookingsAfterDepartureUnit;
			}
			if (isNotNullOrUndefined(attributes.sendThankYouEmail)) {
				this.sendThankYouEmail = attributes.sendThankYouEmail;
			}
			if (isNotNullOrUndefined(attributes.sendThankYouEmailNumberOfDaysAfterDeparture)) {
				this.sendThankYouEmailNumberOfDaysAfterDeparture =
					attributes.sendThankYouEmailNumberOfDaysAfterDeparture;
			}
		}
	}
}
